import { adminDocumentHref, legacyDestination, normalizeBasePath, parseRoute, routeHref, routeMetadata, routePreviewImage, siteOrigin, structuredData, withBasePath, type ToolCollection, type View } from './routes';
import { getLocale, setLocale } from '../i18n/react';
import { translate, type Locale } from '../i18n/core';

const basePath = normalizeBasePath(document.querySelector<HTMLMetaElement>('meta[name="designforge-base"]')?.content ?? '');
const changeEvent = 'designforge:navigate';

export const appHref = (path: string) => withBasePath(path, basePath);
export const hrefFor = (view: View, tool?: string, collection?: ToolCollection) => routeHref(view, tool, collection, basePath, getLocale());

export function readBrowserRoute() { return parseRoute(window.location.pathname, window.location.search, basePath); }

export function languageHref(locale: Locale) {
  const route = readBrowserRoute();
  return routeHref(route.view, route.tool?.slug, undefined, basePath, locale) + window.location.search + window.location.hash;
}

function normalizeLocation() {
  const legacy = legacyDestination(window.location.hash, window.location.search, basePath, readBrowserRoute().locale ?? 'vi');
  const destination = new URL(legacy ?? window.location.href, window.location.origin);
  if (parseRoute(destination.pathname, destination.search, basePath).view === 'admin') {
    window.location.replace(adminDocumentHref(basePath, destination.search));
    return false;
  }
  if (legacy) window.history.replaceState(window.history.state, '', legacy);
  const route = readBrowserRoute();
  // A shared/stored preference must never replace the language in a public URL.
  setLocale(route.locale ?? 'vi', false);
  if (!route.notFound) {
    const canonical = hrefFor(route.view, route.tool?.slug) + window.location.search + window.location.hash;
    if (canonical !== window.location.pathname + window.location.search + window.location.hash) window.history.replaceState(window.history.state, '', canonical);
  }
  return true;
}

export function navigateTo(view: View, tool?: string, collection?: ToolCollection, options?: { replace?: boolean }) {
  if (view === 'admin') { window.location.assign(adminDocumentHref(basePath)); return; }
  const destination = hrefFor(view, tool, collection);
  if (destination === window.location.pathname + window.location.search + window.location.hash) return;
  if (options?.replace) window.history.replaceState(null, '', destination);
  else window.history.pushState(null, '', destination);
  window.dispatchEvent(new Event(changeEvent));
}

export function subscribeNavigation(onChange: () => void) {
  const onHistory = () => { if (normalizeLocation()) onChange(); };
  window.addEventListener('popstate', onHistory);
  window.addEventListener(changeEvent, onHistory);
  const onHash = () => { if (legacyDestination(window.location.hash) !== undefined) onHistory(); };
  window.addEventListener('hashchange', onHash);
  const onClick = (event: MouseEvent) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!(anchor instanceof HTMLAnchorElement) || anchor.hasAttribute('download') || (anchor.target && anchor.target !== '_self')) return;
    const url = new URL(anchor.href);
    if (url.origin !== window.location.origin || readRoute(url).notFound) return;
    if (url.hash && url.pathname === window.location.pathname && url.search === window.location.search) return;
    if (readRoute(url).view === 'admin') {
      event.preventDefault();
      window.location.assign(adminDocumentHref(basePath, url.search));
      return;
    }
    event.preventDefault();
    if (url.pathname + url.search + url.hash === window.location.pathname + window.location.search + window.location.hash) return;
    window.history.pushState(null, '', url.pathname + url.search + url.hash);
    onHistory();
  };
  document.addEventListener('click', onClick);
  return () => {
    window.removeEventListener('popstate', onHistory);
    window.removeEventListener(changeEvent, onHistory);
    window.removeEventListener('hashchange', onHash);
    document.removeEventListener('click', onClick);
  };
}

function readRoute(url: URL) { return parseRoute(url.pathname, url.search, basePath); }

export function updateRouteMetadata() {
  const route = readBrowserRoute();
  const locale = route.view === 'admin' && !route.notFound ? getLocale() : route.locale ?? 'vi';
  const metadata = routeMetadata(route, locale);
  const canonicalUrl = `${siteOrigin}${metadata.canonicalPath || '/'}`;
  document.documentElement.lang = locale;
  const skipLink = document.querySelector('.skip-link');
  if (skipLink) skipLink.textContent = translate(locale, 'Bỏ qua điều hướng');
  document.title = metadata.title;
  document.querySelector<HTMLMetaElement>('meta[name="description"]')?.setAttribute('content', metadata.description);
  const setMeta = (selector: string, attribute: 'name' | 'property', key: string, value: string) => {
    let element = document.head.querySelector<HTMLMetaElement>(selector);
    if (!element) { element = document.createElement('meta'); element.setAttribute(attribute, key); document.head.appendChild(element); }
    element.content = value;
  };
  let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
  canonical.href = canonicalUrl;
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach(link => link.remove());
  for (const [language, path] of Object.entries(metadata.alternatePaths ?? {})) {
    const alternate = document.createElement('link');
    alternate.rel = 'alternate'; alternate.hreflang = language; alternate.href = `${siteOrigin}${path}`;
    document.head.appendChild(alternate);
  }
  setMeta('meta[property="og:url"]', 'property', 'og:url', canonicalUrl);
  setMeta('meta[property="og:title"]', 'property', 'og:title', metadata.title);
  setMeta('meta[property="og:description"]', 'property', 'og:description', metadata.description);
  const previewImage = routePreviewImage(metadata);
  setMeta('meta[property="og:image"]', 'property', 'og:image', previewImage);
  setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', metadata.title);
  setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', metadata.description);
  setMeta('meta[name="twitter:image"]', 'name', 'twitter:image', previewImage);
  let schema = document.head.querySelector<HTMLScriptElement>('script[data-designforge-schema]');
  if (metadata.noindex) schema?.remove();
  else {
    if (!schema) { schema = document.createElement('script'); schema.type = 'application/ld+json'; schema.dataset.designforgeSchema = 'true'; document.head.appendChild(schema); }
    schema.textContent = JSON.stringify(structuredData(metadata));
  }
  let robots = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
  if (metadata.noindex) {
    if (!robots) { robots = document.createElement('meta'); robots.name = 'robots'; document.head.appendChild(robots); }
    robots.content = 'noindex';
  } else robots?.remove();
}

export function initializeNavigation() {
  if (!normalizeLocation()) return false;
  updateRouteMetadata();
  window.addEventListener('designforge:locale', () => {
    updateRouteMetadata();
    document.querySelector<HTMLIFrameElement>('.pdf-editor-frame')?.contentWindow?.postMessage({ type: 'designforge:locale', locale: getLocale() }, window.location.origin);
  });
  return true;
}
